import type { EmailOtpType } from "@supabase/supabase-js";
import { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Brand } from "../components/Brand";
import { supabase } from "../lib/supabase";

const emailTypes = new Set<string>(["invite", "recovery", "magiclink", "email_change", "email"]);

export function AuthConfirmPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tokenHash = params.get("token_hash")?.trim();
  const type = params.get("type") ?? "";
  const valid = Boolean(
    tokenHash &&
      tokenHash.length <= 2048 &&
      params.getAll("token_hash").length === 1 &&
      params.getAll("type").length === 1 &&
      emailTypes.has(type),
  );
  const invited = type === "invite" || (type === "recovery" && params.get("invited") === "1");
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  async function confirm() {
    if (!valid || !tokenHash || !supabase || inFlight.current || confirmed) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: verificationError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type as EmailOtpType,
      });
      if (verificationError) {
        setError(
          verificationError.status && verificationError.status >= 500
            ? "We could not connect. Please try again."
            : type === "recovery"
              ? "This link has expired or has already been used. Request a new reset link below."
              : "This link has expired or has already been used. Ask your CRM administrator for a new email.",
        );
        return;
      }
      if (!data.session) {
        // Secure email changes may require confirmation from both email addresses.
        setConfirmed(true);
        navigate("/auth/confirm?confirmed=1", { replace: true });
        return;
      }
      navigate(
        invited
          ? "/settings?invited=1"
          : type === "recovery"
            ? "/auth/reset-password"
            : "/settings",
        { replace: true },
      );
    } catch {
      setError("We could not connect. Check your connection and try again.");
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const completed = confirmed || params.get("confirmed") === "1";
  return (
    <main className="login-page">
      <section className="login-card">
        <Brand />
        <div className="login-card__intro">
          <p className="eyebrow">Just Kasz CRM</p>
          <h1>
            {completed
              ? "Email confirmed"
              : invited
                ? "Accept your invitation"
                : "Confirm your email link"}
          </h1>
          <p>
            {completed
              ? "If you are changing your email, check the other address for a second confirmation email. Otherwise, you can sign in."
              : invited
                ? "Accept your invitation, then choose a password to finish setting up your account."
                : type === "recovery"
                  ? "Continue to choose a new password for your account."
                  : "Continue to confirm this request for your account."}
          </p>
        </div>
        <div className="form-stack">
          {!completed && !valid && (
            <p className="error-message" role="alert">
              This link is incomplete or invalid. Open the full link from your email, or ask your
              CRM administrator for a new email.
            </p>
          )}
          {!completed && valid && !supabase && (
            <p className="error-message" role="alert">
              Account confirmation is unavailable. Please contact your CRM administrator.
            </p>
          )}
          {error && (
            <p className="error-message" role="alert">
              {error}
            </p>
          )}
          {!completed && valid && supabase && (
            <button
              type="button"
              className="button button--primary"
              disabled={submitting}
              aria-busy={submitting}
              onClick={() => void confirm()}
            >
              {submitting
                ? "Confirming..."
                : invited
                  ? "Accept invitation"
                  : "Confirm and continue"}
            </button>
          )}
          {type === "recovery" && (
            <Link to="/auth/forgot-password" replace>
              Request a new reset link
            </Link>
          )}
          <Link to="/" replace>
            Back to sign in
          </Link>
        </div>
      </section>
      <aside className="login-page__brand-panel" aria-hidden="true">
        <div className="brand-panel__ring" />
        <p>Good food. Good people. Better follow-through.</p>
      </aside>
    </main>
  );
}
